import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Link,
  Grid
} from '@mui/material';
import {
  Edit,
  Delete,
  Add,
  FilterAltOff
} from '@mui/icons-material';
import { cityRegistrationAPI, citiesAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import browserGlobal from '../../utils/browserGlobal';

const CityRegistration = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser.role || '');

  const [registrations, setRegistrations] = useState({ payload: [], totalRecord: 0 });
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentRegistration, setCurrentRegistration] = useState({
    id: 0,
    cityId: '',
    title: '',
    description: '',
    url: '',
    isEnabled: true
  });

  const fetchCities = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const citiesList = response.data.payload;
        setCities(citiesList);

        if (citiesList.length > 0) {
          const firstCity = citiesList[0];
          setSelectedCity(firstCity.id);
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchRegistrations = async () => {
    if (!selectedCity) return;

    setIsLoading(true);
    try {
      const response = await cityRegistrationAPI.getCityRegistrations(selectedCity, page, rowsPerPage);
      console.log('Registration response:', response);
      
      if (response?.data?.success === true) {
        let registrationData = [];
        
        // Handle both array and single object responses
        if (response.data.payload) {
          if (Array.isArray(response.data.payload)) {
            registrationData = response.data.payload;
          } else if (typeof response.data.payload === 'object') {
            registrationData = [response.data.payload];
          }
        }

        console.log('Processed registration data:', registrationData);
        
        setRegistrations({
          payload: registrationData,
          totalRecord: response.data.totalRecord || registrationData.length
        });
        setTotalCount(response.data.totalRecord || registrationData.length);
      } else {
        console.error('API response not successful:', response);
        setRegistrations({ payload: [], totalRecord: 0 });
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      setRegistrations({ payload: [], totalRecord: 0 });
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      fetchRegistrations();
    }
  }, [selectedCity, page, rowsPerPage]);

  const handleOpen = (registration = null) => {
    if (registration) {
      setCurrentRegistration({
        ...registration,
        cityId: selectedCity
      });
      setEditMode(true);
    } else {
      setCurrentRegistration({
        id: 0,
        title: '',
        description: '',
        url: '',
        isEnabled: true,
        cityId: selectedCity
      });
      setEditMode(false);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditMode(false);
  };

  const handleChange = (e) => {
    setCurrentRegistration({
      ...currentRegistration,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    if (!currentRegistration.title || !currentRegistration.description) {
      alert(t('cityReg.toastFill'));
      return;
    }

    try {
      const data = {
        ...currentRegistration,
        cityId: selectedCity
      };
      console.log('Submitting data:', data); // Debug log

      if (editMode) {
        await cityRegistrationAPI.updateCityRegistration(data);
      } else {
        await cityRegistrationAPI.createCityRegistration(data);
      }
      await fetchRegistrations();
      handleClose();
    } catch (error) {
      console.error('Error saving registration:', error);
      alert(t('cityReg.saveError'));
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('cityReg.confirmDelete'))) {
      try {
        await cityRegistrationAPI.deleteCityRegistration(id);
        fetchRegistrations();
      } catch (error) {
        console.error('Error deleting registration:', error);
        alert(t('cityReg.deleteError'));
      }
    }
  };

  const handleClearToolbar = () => {
    if (cities.length > 0) {
      setSelectedCity(cities[0].id);
      setPage(0);
    }
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">
          {t('cityReg.title')}
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('cityReg.add')}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('cityReg.selectCity')}</InputLabel>
              <Select
                value={selectedCity}
                label={t('cityReg.selectCity')}
                onChange={(e) => {
                  setSelectedCity(e.target.value);
                  setPage(0);
                }}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearToolbar}
              sx={clearFilterButtonSx}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <>
          <TableContainer 
            component={Paper} 
            sx={{
              mt: 3,
              boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
              borderRadius: 2,
              '& .MuiTableCell-head': {
                backgroundColor: '#ebf4f4',
                color: '#0F3D3E',
                fontWeight: 600
              },
              '& .MuiTableRow-root:hover': {
                backgroundColor: '#ebf4f4'
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.title')}</TableCell>
                  <TableCell>{t('common.description')}</TableCell>
                  <TableCell>{t('cityReg.fieldUrl')}</TableCell>
                  <TableCell>{t('common.status')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrations.payload.map((registration) => (
                  <TableRow key={registration.id}>
                    <TableCell>{registration.title}</TableCell>
                    <TableCell>
                      <Typography 
                        sx={{ 
                          maxWidth: 300,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={registration.description}
                      >
                        {registration.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {registration.url && (
                        <Link 
                          href={registration.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {registration.url}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={registration.isEnabled ? t('common.active') : t('common.inactive')}
                        color={registration.isEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpen(registration)}
                        sx={{ color: 'primary.main', mr: 1 }}
                      >
                        <Edit />
                      </IconButton>
                      {isSuperAdmin && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(registration.cityId)}
                          sx={{ color: 'error.main' }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number.parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={t('common.rowsPerPage')}
              sx={{
                '.MuiTablePagination-select': {
                  color: '#0F3D3E',
                },
                '.MuiTablePagination-selectIcon': {
                  color: '#0F3D3E',
                },
                '.MuiTablePagination-displayedRows': {
                  color: '#0F3D3E',
                },
                '.MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    color: '#0F3D3E',
                  },
                  '& .Mui-disabled': {
                    color: '#e9d5ff',
                  }
                }
              }}
            />
          </Box>
        </>
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? t('cityReg.dialogEdit') : t('cityReg.dialogAdd')}</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.title')}
                  name="title"
                  value={currentRegistration.title}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.description')}
                  name="description"
                  value={currentRegistration.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('cityReg.fieldUrl')}
                  name="url"
                  value={currentRegistration.url}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentRegistration.isEnabled}
                      onChange={(e) => handleChange({
                        target: {
                          name: 'isEnabled',
                          value: e.target.checked
                        }
                      })}
                      color="primary"
                    />
                  }
                  label={t('accommodation.fieldEnabled')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              }
            }}
          >
            {editMode ? t('common.update') : t('common.add')}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default CityRegistration;
